import { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { emitTypingStart, emitTypingStop } from '../../../services/socket';
import { MentionEditor, defaultDisplayName, excludeUserId } from '../../../components/mentions';
import { useChatAttachment } from '../../../hooks/useChatAttachment';
import {
    CHAT_UPLOAD_ACCEPT,
    CHAT_UPLOAD_HINT,
    formatFileSize,
    getAttachmentKind,
    ATTACHMENT_KIND_LABELS,
} from '../../../common/chatUploads';
import {
    HiOutlinePaperClip,
    HiOutlineEmojiHappy,
    HiOutlineDocumentText,
    HiOutlinePhotograph,
    HiOutlineX,
    HiOutlinePlus,
    HiOutlineCamera,
    HiPaperAirplane,
} from '../../../icons';
import { ChatReplyPreview } from './ChatReplyPreview';
import { ChatUrlModal } from './ChatUrlModal';
import { parseChatUrlMessage } from '../../../common/urlValid';

const EMOJI_PANEL_ID = 'chat-emoji-panel';
const EMOJI_PANEL_W = 320;
const EMOJI_PANEL_H = 380;

const KIND_ICONS = {
    image: HiOutlinePhotograph,
    pdf: HiOutlineDocumentText,
    word: HiOutlineDocumentText,
    file: HiOutlineDocumentText,
};

function AttachmentDraft({ attachment, onRemove }) {
    const kind = getAttachmentKind(attachment.mimeType);
    const Icon = KIND_ICONS[kind] || HiOutlineDocumentText;
    const uploading = attachment.status === 'uploading';

    return (
        <div className="chat-attachment-draft">
            <div className="chat-attachment-draft__preview">
                {attachment.previewUrl ? (
                    <img src={attachment.previewUrl} alt="" className="chat-attachment-draft__thumb" />
                ) : (
                    <Icon className="chat-attachment-draft__icon" aria-hidden="true" />
                )}
            </div>

            <div className="chat-attachment-draft__info">
                <span className="chat-attachment-draft__name" title={attachment.fileName}>
                    {attachment.fileName}
                </span>
                <span className="chat-attachment-draft__meta">
                    {ATTACHMENT_KIND_LABELS[kind]}
                    {attachment.size ? ` · ${formatFileSize(attachment.size)}` : ''}
                    {uploading ? ` · Subiendo ${attachment.progress}%` : ' · Listo para enviar'}
                </span>
                {uploading && (
                    <span
                        className="chat-attachment-draft__progress"
                        role="progressbar"
                        aria-valuenow={attachment.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Subiendo ${attachment.fileName}`}
                    >
                        <span
                            className="chat-attachment-draft__progress-bar"
                            style={{ width: `${attachment.progress}%` }}
                        />
                    </span>
                )}
            </div>

            <button
                type="button"
                className="chat-attachment-draft__remove"
                onClick={onRemove}
                title={uploading ? 'Cancelar subida' : 'Quitar archivo'}
                aria-label={uploading ? 'Cancelar subida' : 'Quitar archivo'}
            >
                <HiOutlineX className="chat-attachment-draft__remove-icon" aria-hidden="true" />
            </button>
        </div>
    );
}

export const MessageInput = ({
    courseId,
    conversationId,
    replyTo,
    onCancelReply,
    participants = [],
    currentUserId,
    onSend,
    onComposingChange,
}) => {
    const [content, setContent] = useState('');
    const [taggedUserIds, setTaggedUserIds] = useState([]);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [emojiPos, setEmojiPos] = useState({ top: 0, left: 0 });
    const [dragging, setDragging] = useState(false);
    const [isTypingActive, setIsTypingActive] = useState(false);
    const [urlModalOpen, setUrlModalOpen] = useState(false);
    const [urlSending, setUrlSending] = useState(false);
    const typingTimeoutRef = useRef(null);
    const mentionKeyRef = useRef(0);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const emojiBtnRef = useRef(null);
    const dragDepthRef = useRef(0);

    const { attachment, isUploading, startUpload, clearAttachment } = useChatAttachment(courseId);

    const mentionableUsers = useMemo(
        () => participants.filter(excludeUserId(currentUserId)),
        [participants, currentUserId],
    );

    const attachmentReady = attachment?.status === 'ready';
    const canSend = (Boolean(content.trim()) || attachmentReady) && !isUploading;

    useEffect(() => {
        onComposingChange?.(Boolean(content.trim()) || isTypingActive);
    }, [content, isTypingActive, onComposingChange]);

    useEffect(() => () => {
        onComposingChange?.(false);
    }, [onComposingChange]);

    const positionEmojiPanel = useCallback(() => {
        const btn = emojiBtnRef.current;
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const gap = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let top = rect.top - EMOJI_PANEL_H - gap;
        if (top < 8) {
            top = rect.bottom + gap;
        }
        if (top + EMOJI_PANEL_H > vh - 8) {
            top = Math.max(8, vh - EMOJI_PANEL_H - 8);
        }

        let left = rect.left + rect.width / 2 - EMOJI_PANEL_W / 2;
        left = Math.max(8, Math.min(left, vw - EMOJI_PANEL_W - 8));

        setEmojiPos({ top, left });
    }, []);

    useLayoutEffect(() => {
        if (!emojiOpen) return;
        positionEmojiPanel();
    }, [emojiOpen, positionEmojiPanel]);

    useEffect(() => {
        if (!emojiOpen) return;

        const onDocDown = (e) => {
            const panel = document.getElementById(EMOJI_PANEL_ID);
            if (emojiBtnRef.current?.contains(e.target)) return;
            if (panel?.contains(e.target)) return;
            setEmojiOpen(false);
        };
        const onReposition = () => positionEmojiPanel();

        document.addEventListener('mousedown', onDocDown);
        window.addEventListener('scroll', onReposition, true);
        window.addEventListener('resize', onReposition);

        return () => {
            document.removeEventListener('mousedown', onDocDown);
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        };
    }, [emojiOpen, positionEmojiPanel]);

    const handleTyping = useCallback(() => {
        if (!courseId || !conversationId) return;

        setIsTypingActive(true);
        emitTypingStart(courseId, conversationId);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            setIsTypingActive(false);
            emitTypingStop(courseId, conversationId);
        }, 2000);
    }, [courseId, conversationId]);

    const resetComposer = useCallback(() => {
        setContent('');
        setTaggedUserIds([]);
        mentionKeyRef.current += 1;
    }, []);

    const handleSend = useCallback(() => {
        if (isUploading) return;

        const trimmed = content.trim();
        if (!trimmed && !attachmentReady) return;

        if (!conversationId) {
            toast.error('No hay conversación activa. Recarga la página.');
            return;
        }

        const payload = attachmentReady ? attachment.payload : null;
        const detectedUrl = !payload ? parseChatUrlMessage(trimmed) : null;

        resetComposer();
        clearAttachment();
        setIsTypingActive(false);
        emitTypingStop(courseId, conversationId);

        onSend({
            conversationId,
            content: detectedUrl || trimmed,
            type: payload
                ? (getAttachmentKind(payload.mimeType) === 'image' ? 'image' : 'file')
                : (detectedUrl ? 'url' : 'text'),
            attachments: payload ? [payload] : [],
            replyTo,
            mentions: taggedUserIds,
        });

        onCancelReply?.();
    }, [
        isUploading,
        content,
        attachmentReady,
        attachment,
        conversationId,
        courseId,
        replyTo,
        taggedUserIds,
        onSend,
        onCancelReply,
        resetComposer,
        clearAttachment,
    ]);

    const insertEmoji = useCallback((emoji) => {
        setContent((prev) => `${prev}${emoji}`);
        handleTyping();
    }, [handleTyping]);

    const handleEmojiClick = useCallback((emojiData) => {
        insertEmoji(emojiData.emoji);
    }, [insertEmoji]);

    const handleAttachClick = useCallback(() => {
        if (isUploading) return;
        if (!conversationId) {
            toast.error('No hay conversación activa.');
            return;
        }
        fileInputRef.current?.click();
    }, [isUploading, conversationId]);

    const handleImageClick = useCallback(() => {
        if (isUploading) return;
        if (!conversationId) {
            toast.error('No hay conversación activa.');
            return;
        }
        imageInputRef.current?.click();
    }, [isUploading, conversationId]);

    const handleOpenUrlModal = useCallback(() => {
        if (!conversationId) {
            toast.error('No hay conversación activa.');
            return;
        }
        setUrlModalOpen(true);
    }, [conversationId]);

    const handleSendUrl = useCallback(async (url) => {
        if (!conversationId || urlSending) return;

        setUrlSending(true);
        try {
            resetComposer();
            setIsTypingActive(false);
            emitTypingStop(courseId, conversationId);

            onSend({
                conversationId,
                content: url,
                type: 'url',
                attachments: [],
                replyTo,
                mentions: [],
            });
            setUrlModalOpen(false);
            onCancelReply?.();
        } finally {
            setUrlSending(false);
        }
    }, [conversationId, urlSending, onSend, replyTo, onCancelReply, resetComposer, courseId]);

    const uploadFile = useCallback(async (file) => {
        const result = await startUpload(file);
        if (!result.ok && result.message) {
            toast.error(result.message);
        }
    }, [startUpload]);

    const handleFileSelected = useCallback((e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) uploadFile(file);
    }, [uploadFile]);

    const handleDragEnter = useCallback((e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        dragDepthRef.current += 1;
        setDragging(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragging(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        dragDepthRef.current = 0;
        setDragging(false);

        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(file);
    }, [uploadFile]);

    const handlePaste = useCallback((e) => {
        const file = Array.from(e.clipboardData?.files || [])[0];
        if (!file) return;
        e.preventDefault();
        uploadFile(file);
    }, [uploadFile]);

    return (
        <div
            className={`chat-composer ${dragging ? 'chat-composer--dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onPaste={handlePaste}
        >
            {replyTo && (
                <ChatReplyPreview
                    replyTo={replyTo}
                    participants={participants}
                    currentUserId={currentUserId}
                    variant="composer"
                    onDismiss={onCancelReply}
                />
            )}

            {attachment && (
                <AttachmentDraft attachment={attachment} onRemove={clearAttachment} />
            )}

            {dragging && (
                <div className="chat-composer__dropzone" aria-hidden="true">
                    Suelta el archivo para adjuntarlo
                </div>
            )}

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSend();
                }}
                className="chat-composer__bar"
            >
                <button
                    type="button"
                    className="chat-composer__plus-btn"
                    title={CHAT_UPLOAD_HINT}
                    aria-label={`Adjuntar archivo. ${CHAT_UPLOAD_HINT}`}
                    onClick={handleAttachClick}
                    disabled={isUploading}
                >
                    <HiOutlinePlus className="chat-composer__plus-icon" aria-hidden="true" />
                </button>

                <span className="chat-composer__divider" aria-hidden="true" />

                <div className="chat-composer__input-wrap">
                    <MentionEditor
                        key={mentionKeyRef.current}
                        id="chat-message-input"
                        className="chat-composer__editor-host"
                        value={content}
                        onValueChange={setContent}
                        taggedUserIds={taggedUserIds}
                        onTaggedUserIdsChange={setTaggedUserIds}
                        users={mentionableUsers}
                        getDisplayName={defaultDisplayName}
                        filterUser={() => true}
                        placeholder={attachment ? 'Agrega un comentario…' : 'Escribe un mensaje…'}
                        rows={1}
                        appearance="embedded"
                        menuPlacement="auto"
                        submitOnEnter
                        onSubmit={handleSend}
                        onTyping={handleTyping}
                    />
                </div>

                <div className="chat-composer__actions">
                    <button
                        type="button"
                        className={`chat-composer__tool ${isUploading ? 'chat-composer__tool--active' : ''}`}
                        title="Subir imagen"
                        aria-label="Subir imagen"
                        onClick={handleImageClick}
                        disabled={isUploading}
                    >
                        <HiOutlineCamera className="chat-composer__tool-icon" aria-hidden="true" />
                    </button>

                    <button
                        type="button"
                        className="chat-composer__tool"
                        title="Compartir enlace"
                        aria-label="Compartir enlace"
                        onClick={handleOpenUrlModal}
                    >
                        <HiOutlinePaperClip className="chat-composer__tool-icon" aria-hidden="true" />
                    </button>

                    <button
                        ref={emojiBtnRef}
                        type="button"
                        className={`chat-composer__tool ${emojiOpen ? 'chat-composer__tool--active' : ''}`}
                        title="Emoji"
                        aria-label="Insertar emoji"
                        aria-expanded={emojiOpen}
                        aria-controls={EMOJI_PANEL_ID}
                        onClick={() => setEmojiOpen((o) => !o)}
                    >
                        <HiOutlineEmojiHappy className="chat-composer__tool-icon" aria-hidden="true" />
                    </button>

                    {emojiOpen && createPortal(
                        <div
                            id={EMOJI_PANEL_ID}
                            className="chat-composer__emoji-panel chat-composer__emoji-panel--portal"
                            style={{ top: emojiPos.top, left: emojiPos.left }}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                theme={Theme.LIGHT}
                                width={EMOJI_PANEL_W}
                                height={EMOJI_PANEL_H}
                                searchPlaceholder="Buscar emoji…"
                                lazyLoadEmojis
                                previewConfig={{ showPreview: false }}
                            />
                        </div>,
                        document.body,
                    )}

                    <button
                        type="submit"
                        disabled={!canSend}
                        className="chat-composer__send-circle"
                        aria-label="Enviar mensaje"
                    >
                        <HiPaperAirplane className="chat-composer__send-circle-icon" aria-hidden="true" />
                    </button>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    className="sr-only"
                    accept={CHAT_UPLOAD_ACCEPT}
                    onChange={handleFileSelected}
                    tabIndex={-1}
                />
                <input
                    ref={imageInputRef}
                    type="file"
                    className="sr-only"
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    onChange={handleFileSelected}
                    tabIndex={-1}
                />
            </form>

            <ChatUrlModal
                open={urlModalOpen}
                onClose={setUrlModalOpen}
                onSubmit={handleSendUrl}
                loading={urlSending}
            />
        </div>
    );
};
